import { throwIfAborted } from '@admin-panel/utils';
// Bounded codec/container admission only; unsupported media keeps the existing Stream fallback.
const MAX_METADATA_BYTES = 2 * 1024 * 1024;
const ANCILLARY_HANDLERS = new Set(['tmcd', 'text', 'sbtl', 'subt', 'clcp', 'meta', 'hint', 'sdsm', 'odsm']);
const text = (bytes: Uint8Array, offset: number) =>
  String.fromCharCode(...bytes.subarray(offset, offset + 4));

// hdlr is a sibling of minf and can follow it. Undefined preserves the old
// conservative codec-only check; an invalid/duplicate handler fails closed.
function trackHandler(bytes: Uint8Array, start: number, end: number): string | undefined {
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let handler: string | undefined;
  for (let pos = start; pos < end;) {
    if (end - pos < 8) return '';
    const size = view.getUint32(pos);
    if (size < 8 || size > end - pos) return '';
    if (text(bytes, pos + 4) === 'hdlr') {
      if (handler !== undefined || size < 32 || bytes[pos + 8] !== 0) return '';
      handler = text(bytes, pos + 16);
    }
    pos += size;
  }
  return handler;
}

function avcTracks(bytes: Uint8Array, start = 0, end = bytes.length, depth = 0, handler?: string): number {
  if (depth > 5) return -1;
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
  let videos = 0;
  for (let pos = start; pos < end;) {
    if (end - pos < 8) return -1;
    const size = view.getUint32(pos);
    if (size < 8 || size > end - pos) return -1;
    const next = pos + size, type = text(bytes, pos + 4);
    if (type === 'mdia') {
      const nextHandler = trackHandler(bytes, pos + 8, next);
      if (nextHandler !== undefined && nextHandler !== 'vide' && nextHandler !== 'soun'
        && !ANCILLARY_HANDLERS.has(nextHandler)) return -1;
      const found = avcTracks(bytes, pos + 8, next, depth + 1, nextHandler);
      if (found < 0) return -1;
      videos += found;
    } else if (type === 'stsd') {
      if (size < 16 || bytes[pos + 8] !== 0) return -1;
      const entries = view.getUint32(pos + 12);
      if (entries < 1 || entries > 8) return -1;
      let sample = pos + 16;
      for (let i = 0; i < entries; i++) {
        if (next - sample < 8) return -1;
        const sampleSize = view.getUint32(sample);
        if (sampleSize < 8 || sampleSize > next - sample) return -1;
        const codec = text(bytes, sample + 4);
        if (handler === 'vide' || (handler === undefined && (codec === 'avc1' || codec === 'avc3'))) {
          if ((codec !== 'avc1' && codec !== 'avc3') || sampleSize < 86) return -1;
          videos++;
        } else if ((handler === 'soun' || handler === undefined) && (codec !== 'mp4a' || sampleSize < 36)) return -1;
        sample += sampleSize;
      }
      if (sample !== next) return -1;
    } else if (['trak', 'minf', 'stbl'].includes(type)) {
      const found = avcTracks(bytes, pos + 8, next, depth + 1, handler);
      if (found < 0) return -1;
      videos += found;
    }
    pos = next;
  }
  return videos;
}

export async function isDirectMp4(file: Blob, signal?: AbortSignal): Promise<boolean> {
  if (file.type.split(';')[0].trim().toLowerCase() !== 'video/mp4') return false;
  // FileReader also supports older mobile WebViews without Blob.arrayBuffer.
  const read = async (offset: number, length: number): Promise<Uint8Array> => {
    throwIfAborted(signal);
    const blob = file.slice(offset, offset + length);
    const buffer = typeof blob.arrayBuffer === 'function' ? await blob.arrayBuffer() :
      await new Promise<ArrayBuffer>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as ArrayBuffer);
        reader.onerror = () => reject(reader.error);
        reader.readAsArrayBuffer(blob);
      });
    throwIfAborted(signal);
    return new Uint8Array(buffer);
  };
  try {
    let offset = 0, ftyp = false, mdat = false, video = false;
    for (let count = 0; count < 8 && offset + 8 <= file.size; count++) {
      const header = await read(offset, Math.min(16, file.size - offset));
      if (header.length < 8) return false;
      const view = new DataView(header.buffer, header.byteOffset, header.byteLength);
      let size = view.getUint32(0), prefix = 8;
      const type = text(header, 4);
      if (size === 1) {
        if (header.length < 16) return false;
        // Avoid BigInt requirements on older mobile targets.
        size = view.getUint32(8) * 2 ** 32 + view.getUint32(12);
        prefix = 16;
      } else if (size === 0) size = file.size - offset;
      if (!Number.isSafeInteger(size) || size < prefix || size > file.size - offset) return false;
      if (type === 'ftyp') ftyp = size >= prefix + 8;
      if (type === 'mdat') mdat = size > prefix;
      if (type === 'moov') {
        if (size > MAX_METADATA_BYTES) return false;
        video = avcTracks(await read(offset + prefix, size - prefix)) > 0;
        if (!video) return false;
      }
      if (ftyp && mdat && video) return true;
      offset += size;
    }
    return false;
  } catch (error) {
    throwIfAborted(signal);
    return false; // Cannot verify locally: retain the compatible legacy upload path.
  }
}
